import * as THREE from 'three';
import { SKETCH_STYLE_COLORS } from '../themes/colors';

/**
 * Backface shading — the SketchUp backface language, app-wide and permanent.
 *
 * Reversed faces (tunnel walls seen from inside, an open shell's interior,
 * DoubleSide BIM / NURBS / SUBD surfaces) must read as a fixed DARKER tone
 * while KEEPING the scene's shading: with plain DoubleSide lighting the
 * flipped normals point away from the lights and the faces collapse to pure
 * black under ACES tone mapping. So the tint swaps the back side's DIFFUSE
 * color before lighting instead of painting over the final pixel — sun
 * direction, shadows and the ambient/hemisphere grade stay visible on the
 * back face, and it reads as "the same surface seen from behind".
 *
 * Mechanism (installed ONCE, on first import):
 * - `uBackfaceTint` joins the shared <common> chunk, so every shader can
 *   reference it (unused declarations are stripped by the GLSL compiler).
 * - A branch appended to the shared <color_fragment> chunk swaps the back
 *   side's diffuse, guarded by the USE_BACKFACE_TINT define: materials opt
 *   in with `defines` (BACKFACE_DEFINES), everything else — lines, points,
 *   overlays, markers, tool ghosts — compiles the branch out untouched.
 * - The uniform value is a global constant, so it lives in each surface
 *   shader's ShaderLib uniform set (basic/lambert/phong/toon/matcap/physical)
 *   and is uploaded like any built-in uniform.
 *
 * The guard also keys the program cache (three includes material.defines in
 * the cache key), so patched and unpatched materials never share a program.
 * Every workbench (Sketch, 2D, 3D, BIM) renders solids through these shared
 * chunks, so opting in per solid renderer covers the whole app — and any
 * future DoubleSide surface material inherits the language by adding the
 * same define.
 */

/** Opt-in flag object for any three material (spread into `defines`). */
export const BACKFACE_DEFINES: { [key: string]: string } = { USE_BACKFACE_TINT: '' };

const TINTED_SHADER_LIBS = ['basic', 'lambert', 'phong', 'toon', 'matcap', 'physical'] as const;

let installed = false;

function install(): void {
    if (installed) return;
    installed = true;

    const tint = new THREE.Color(SKETCH_STYLE_COLORS.backface);

    THREE.ShaderChunk.common = `uniform vec3 uBackfaceTint;\n${THREE.ShaderChunk.common}`;
    THREE.ShaderChunk.color_fragment = `${THREE.ShaderChunk.color_fragment}
#ifdef USE_BACKFACE_TINT
	if ( ! gl_FrontFacing ) diffuseColor.rgb = uBackfaceTint;
#endif
`;
    for (const id of TINTED_SHADER_LIBS) {
        const lib = THREE.ShaderLib[id] as { uniforms: Record<string, unknown> } | undefined;
        if (lib) lib.uniforms.uBackfaceTint = { value: tint };
    }
}

install();
